import {
	ReactElement,
	useState,
	useRef,
	useMemo,
	Fragment,
	useCallback,
	RefObject
} from "react";
import { Action, ActionAndOthers } from "client_legacy/ndaw/actions";
import { IconButton, DropOverActionList } from "client/Components";
import {
	actionComponentFactory,
	computeActionsToRender
} from "client/Components/ActionList/actionsRenderHelpers";
import styles from "./SubNavigationHeader.module.less";
import { generateTrackingId } from "client/Misc/utils";
import { MenuItemKind } from "./GlobalNavigationSidebar";

export type SubNavigationHeaderProps<TActionContext> = {
	title: string;
	kind: MenuItemKind;
} & (
	{
		actions?: undefined;
	}
	| {
		actions: readonly Action<TActionContext>[];
		actionContext: TActionContext;
	}
);

export const SubNavigationHeader = <TActionContext,>(props: SubNavigationHeaderProps<TActionContext>): ReactElement => {
	const {
		title,
		kind
	} = props;

	return (
		<div className={styles.root}>
			<h3 className={styles.title}>
				{title}
			</h3>
			{props.actions && (
				<SubNavigationActions
					actionContext={props.actionContext}
					actions={props.actions}
					kind={kind}
					menuTitle={title}
				/>
			)}
		</div>
	);
};

SubNavigationHeader.displayName="SubNavigation.Header";

type SubNavigationActionsProps<TActionContext> = {
	actions: readonly Action<TActionContext>[];
	actionContext: TActionContext;
	kind: MenuItemKind;
	menuTitle: string;
};

const SubNavigationActions = <TActionContext,>(
	props: SubNavigationActionsProps<TActionContext>
): ReactElement | null => {
	const {
		actions,
		actionContext,
		kind,
		menuTitle
	} = props;

	const [isActionDropOverOpen, setIsActionDropOverOpen] = useState<boolean>(false);

	const actionsMenuButton: RefObject<HTMLButtonElement | null> =
		useRef<HTMLButtonElement>(null);

	const actionsToRender = useMemo(
		(): readonly ActionAndOthers<TActionContext>[] => {
				return computeActionsToRender(
					actions,
					actionContext
				);
			},
		[
			actions,
			actionContext
		]
	);

	const closeDropOverHandler = useCallback(
		(): void => {
			setIsActionDropOverOpen(false);
		},
		[]
	);

	const trackingId: string = generateTrackingId("sub-navigation-header-more-button", `|${kind}`);

	if (actionsToRender.length === 0) {
		return null;
	}

	return (
		<>
			<IconButton
				trackingId={trackingId}
				kind="transparent"
				icon="ellipsis"
				onClick={() => setIsActionDropOverOpen(true)}
				ref={actionsMenuButton}
				shape="round"
			/>

			{isActionDropOverOpen && actionsMenuButton.current && (
				<DropOverActionList
					trackingId={generateTrackingId(trackingId, `__dropOver`)}
					target={actionsMenuButton.current}
					onClose={closeDropOverHandler}
					bubbleClick={false}
					menuTitle={menuTitle}
				>
					{actionsToRender.map((x) => (
						<Fragment key={x.name}>
							{actionComponentFactory(x, props.actionContext)}
						</Fragment>
					))}
				</DropOverActionList>
			)}
		</>
	);
};

SubNavigationActions.displayName = "SubNavigationHeader.Actions";